import { publications } from "../data/publications";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const normalizeTitle = (title = "") =>
  String(title)
    .replace(/['"“”‘’]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const isArxivUrl = (href = "") =>
  /arxiv\.org|\/arxiv\./i.test(href);

const getPreprintLabel = (href) =>
  isArxivUrl(href) ? "arXiv Preprint" : "Preprint";

export const parseArxivDate = (url = "") => {
  const absMatch = url.match(/arxiv\.org\/abs\/(\d{2})(\d{2})\./i);
  if (absMatch) {
    return {
      year: 2000 + Number(absMatch[1]),
      month: Number(absMatch[2]),
    };
  }

  const doiMatch = url.match(/arxiv\.(\d{2})(\d{2})\./i);
  if (doiMatch) {
    return {
      year: 2000 + Number(doiMatch[1]),
      month: Number(doiMatch[2]),
    };
  }

  return null;
};

export const formatPublicationDateLabel = ({ year, month, links } = {}) => {
  let resolvedYear = year;
  let resolvedMonth = month;

  if (!resolvedMonth) {
    const parsed =
      parseArxivDate(links?.preprint) || parseArxivDate(links?.paper);
    if (parsed) {
      resolvedYear = resolvedYear || parsed.year;
      resolvedMonth = parsed.month;
    }
  }

  if (resolvedMonth && resolvedYear) {
    return `${MONTH_ABBR[resolvedMonth - 1]} ${resolvedYear}`;
  }

  if (resolvedYear) return String(resolvedYear);
  return null;
};

export const getPublicationSortKey = ({ year, month, links } = {}) => {
  let resolvedYear = year || 0;
  let resolvedMonth = month;

  if (!resolvedMonth) {
    const parsed =
      parseArxivDate(links?.preprint) || parseArxivDate(links?.paper);
    if (parsed) {
      resolvedYear = resolvedYear || parsed.year;
      resolvedMonth = parsed.month;
    }
  }

  return resolvedYear * 100 + (resolvedMonth || 0);
};

export const findPublicationByTitle = (query) => {
  const normalizedQuery = normalizeTitle(query);
  if (!normalizedQuery) return null;

  return (
    publications.find((pub) => normalizeTitle(pub.title) === normalizedQuery) ||
    publications.find(
      (pub) =>
        normalizeTitle(pub.title).includes(normalizedQuery) ||
        normalizedQuery.includes(normalizeTitle(pub.title)),
    )
  );
};

export const getPrimaryPublicationLink = (links = {}) =>
  links.paper || links.preprint || links.pdf || links.poster || null;

export const getPublicationLinkEntries = (links = {}) => {
  const entries = [];
  if (links.paper) {
    entries.push({ href: links.paper, label: "DOI / Publisher Link" });
  }
  if (links.preprint) {
    entries.push({
      href: links.preprint,
      label: getPreprintLabel(links.preprint),
    });
  }
  if (links.pdf) entries.push({ href: links.pdf, label: "PDF" });
  if (links.poster) entries.push({ href: links.poster, label: "Poster" });
  if (links.slides) entries.push({ href: links.slides, label: "Slides" });
  return entries;
};

export const resolveProjectPublications = (publicationRefs = []) =>
  publicationRefs
    .map((ref) => {
      const title = typeof ref === "string" ? ref : ref.title;
      const label = typeof ref === "string" ? null : ref.label;
      const matched = findPublicationByTitle(title);
      const links = matched?.links || {};
      const dateFields = {
        year: matched?.year,
        month: matched?.month,
        links,
      };

      return {
        title: matched?.title || title,
        label,
        dateLabel: formatPublicationDateLabel(dateFields),
        sortKey: getPublicationSortKey(dateFields),
        image: matched?.image,
        links,
        href: getPrimaryPublicationLink(links),
        linkEntries: getPublicationLinkEntries(links),
      };
    })
    .sort((a, b) => b.sortKey - a.sortKey);
